/**
 * @fileoverview Own focused editing of matter-linked opposing-counsel/firm contact records. The backend owns accepted party relationships and the parent reconciles case context. Local card/edit state remains separate from saved identity and from conflict-of-interest review.
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import ToastContext from '../../ToastContext';
import api from '../../api';
import { AppButton, AppTextInput, AppTextarea } from '../shared';
import './PartiesPanel.css';

const EMPTY_OPPOSING_COUNSEL = Object.freeze({
	Name: '',
	Email: '',
	Phone: '',
	AdditionalEmails: '',
	AdditionalPhone: '',
	Notes: '',
})

const FIELD_CONFIG = [
	{ label: 'Name', key: 'Name', type: 'text' },
	{ label: 'Email', key: 'Email', type: 'text' },
	{ label: 'Phone', key: 'Phone', type: 'text' },
	{ label: 'Additional Emails', key: 'AdditionalEmails', type: 'text' },
	{ label: 'Additional Phones', key: 'AdditionalPhone', type: 'text' },
	{ label: 'Notes', key: 'Notes', type: 'textarea', fullWidth: true },
]



function hasOpposingCounselData(opposingCounsel) {
	return FIELD_CONFIG.some(({ key }) => {
		const value = opposingCounsel?.[key]
		return typeof value === 'string' ? value.trim().length > 0 : Boolean(value)
	})
}



function buildEditableOpposingCounsel(opposingCounsel) {
	return {
		...EMPTY_OPPOSING_COUNSEL,
		...(opposingCounsel || {}),
	}
}



function OpposingCounsel({ opposingCounsel, setOpposingCounsel, matterNumber, fetchMatter, embedded = false, tabbed = false }) {
	const showToast = useContext(ToastContext)
	const [isEditing, setIsEditing] = useState(false)
	const [pendingFocusField, setPendingFocusField] = useState(null)
	const hasSavedData = hasOpposingCounselData(opposingCounsel)
	const compactLayout = embedded || tabbed
	const editableOpposingCounsel = useMemo(
		() => buildEditableOpposingCounsel(opposingCounsel),
		[opposingCounsel]
	)

	const handleStartEditing =

	() => {
		setOpposingCounsel(editableOpposingCounsel)
		setIsEditing(true)
	}

	const handleStartEditingField =

	(fieldKey) => {
		if (!isEditing) {
			setPendingFocusField(fieldKey)
			handleStartEditing()
		}
	}

	const handleInputChange =

	(key, value) => {
		setOpposingCounsel({
			...editableOpposingCounsel,
			[key]: value,
		})
	}

	const handleSave =

	async () => {
		try {
			const response = await api.post('opposing-counsel', {
				MatterNumber: matterNumber,
				...editableOpposingCounsel,
			})
			await fetchMatter()
			setIsEditing(false)
			setPendingFocusField(null)

			if (response.status !== 201) {
				showToast({ severity: 'error', summary: 'Error', detail: 'Failed to update opposing counsel' })
				return
			}

			showToast({ severity: 'success', summary: 'Successful', detail: 'Updated opposing counsel' })
		} catch (error) {
			console.error(error)
			showToast({ severity: 'error', summary: 'Error', detail: 'An error occurred while updating opposing counsel' })
		}
	}

	const handleCancel =

	async () => {
		setIsEditing(false)
		setPendingFocusField(null)
		await fetchMatter()
	}

	const handleRemove =

	async () => {
		if (!hasSavedData) {
			setOpposingCounsel(null)
			setIsEditing(false)
			setPendingFocusField(null)
			return
		}

		try {
			const response = await api.post('opposing-counsel', { MatterNumber: matterNumber })
			await fetchMatter()
			setIsEditing(false)
			setPendingFocusField(null)

			if (response.status !== 201) {
				showToast({ severity: 'error', summary: 'Error', detail: 'Failed to detach opposing counsel' })
				return
			}

			showToast({ severity: 'success', summary: 'Successful', detail: 'Detached opposing counsel' })
		} catch (error) {
			console.error(error)
			showToast({ severity: 'error', summary: 'Error', detail: 'An error occurred while detaching opposing counsel' })
		}
	}

	const renderEmptyState =

	() => (
		<div className={compactLayout ? 'parties-panel__tab-body parties-panel__tab-body--compact' : ''}>
			<div className="parties-panel__empty-note">No opposing counsel attached.</div>
		</div>
	)

	const renderFields =

	() => (
		<div className={compactLayout ? 'parties-panel__tab-body parties-panel__tab-body--flat' : ''}>
			<div className="parties-panel__field-grid">
				{FIELD_CONFIG.map((field) => {
					const value = editableOpposingCounsel[field.key]
					const fieldId = `opposing-counsel-${field.key}`
					return (
						<div
							key={field.key}
							className={`parties-panel__field${field.fullWidth ? ' parties-panel__field--full' : ''}${field.key === 'Notes' ? ' parties-panel__field--tall' : ''}`}
							onClick={() => handleStartEditingField(field.key)}
						>
							<label htmlFor={fieldId} className="parties-panel__field-label">
								{field.label}
							</label>
							<div className={isEditing ? 'parties-panel__field-control' : 'parties-panel__field-value'}>
								{isEditing ? (
									field.type === 'textarea' ? (
										<AppTextarea
											variant="borderless"
											autosize
											className="parties-panel__compact-textarea"
											id={fieldId}
											autoFocus={pendingFocusField === field.key}
											value={value || ''}
											onChange={(nextValue) => handleInputChange(field.key, nextValue)}
											rows={1}
										/>
									) : (
										<AppTextInput
											className="parties-panel__compact-input"
											id={fieldId}
											variant="borderless"
											autoFocus={pendingFocusField === field.key}
											value={value || ''}
											onChange={(event) => handleInputChange(field.key, event.target.value)}
										/>
									)
								) : value ? (
									<span className={`parties-panel__field-text${field.type === 'textarea' ? ' parties-panel__field-text--multiline' : ''}`}>
										{value}
									</span>
								) : (
									<span className="parties-panel__field-text parties-panel__field-text--muted">Not Available</span>
								)}
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)

	useEffect(() => {
		if (isEditing && pendingFocusField) {
			setPendingFocusField(null)
		}
	}, [isEditing, pendingFocusField])

	const content = (
		<>
			<div className={compactLayout ? 'parties-panel__compact-actions' : 'flex justify-content-end mb-3 gap-2 flex-wrap'}>
				<div className={compactLayout ? 'parties-panel__tab-actions' : 'flex gap-2 flex-wrap'}>
					{isEditing ? (
						<>
							<AppButton className="parties-panel__button" aria-label="Save opposing counsel" icon="pi pi-check" label="Save" variant="success" onClick={handleSave} />
							<AppButton className="parties-panel__button" aria-label="Cancel opposing counsel" icon="pi pi-times" label="Cancel" variant="secondary" onClick={handleCancel} />
						</>
					) : hasSavedData ? (
						<AppButton className="parties-panel__button" aria-label="Edit opposing counsel" icon="pi pi-pencil" variant="secondary" label="Edit" onClick={handleStartEditing} />
					) : (
						<AppButton className="parties-panel__button" aria-label="Add opposing counsel" icon="pi pi-plus" variant="secondary" label="Add Opposing Counsel" onClick={handleStartEditing} />
					)}
				</div>
			</div>

			{!hasSavedData && !isEditing ? renderEmptyState() : renderFields()}

			{isEditing && (
				<div className={compactLayout ? 'parties-panel__tab-body parties-panel__tab-body--actions' : 'mt-4'}>
					<AppButton
						className="parties-panel__button parties-panel__button--danger-muted"
						aria-label={hasSavedData ? 'Detach opposing counsel' : 'Clear opposing counsel'}
						icon="pi pi-times"
						label={hasSavedData ? 'Detach opposing counsel' : 'Clear'}
						variant="secondary"
						onClick={handleRemove}
					/>
				</div>
			)}
		</>
	)

	if (tabbed || embedded) {
		return (
			<div className={`parties-panel__tab-panel${isEditing ? ' is-editing' : ''}`}>
				{content}
			</div>
		)
	}

	return (
		<div className="col-12 xl:col-6">
			<div className="parties-panel__shell h-full">
				{content}
			</div>
		</div>
	)
}

export default OpposingCounsel
